/**
 * A row's or a page's ⋯ menu. It floats (`ui/floating.tsx`) and follows the
 * WAI-ARIA menu pattern like the project switcher: focus moves in, ↑ ↓ Home
 * End move, Escape closes and returns focus to the button. Render it only
 * with at least one item; people who may change nothing get no ⋯ at all.
 */

import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { MoreHorizontal } from "lucide-react";
import { Floating } from "@/components/ui/floating";

export interface MenuItem {
  label: string;
  onSelect: () => void;
}

const itemClass =
  "flex w-full items-center rounded-md px-2.5 py-2 text-left text-[13px] text-body outline-none hover:bg-nav-hover hover:text-ink focus:bg-nav-hover focus:text-ink";

export function RowMenu({
  label,
  items,
  size = "sm",
}: {
  label: string;
  items: MenuItem[];
  size?: "sm" | "md" | undefined;
}) {
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const entries = () => [...(menu.current?.querySelectorAll<HTMLElement>("[role=menuitem]") ?? [])];

  useEffect(() => {
    if (open) entries()[0]?.focus();
  }, [open]);

  const close = (refocus = true) => {
    setOpen(false);
    if (refocus) button.current?.focus();
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") return (e.preventDefault(), e.stopPropagation(), close());
    if (e.key === "Tab") return close(false);
    const list = entries();
    const at = list.indexOf(document.activeElement as HTMLElement);
    const go = (i: number) => (e.preventDefault(), list[(i + list.length) % list.length]?.focus());
    if (e.key === "ArrowDown") go(at + 1);
    else if (e.key === "ArrowUp") go(at <= 0 ? list.length - 1 : at - 1);
    else if (e.key === "Home") go(0);
    else if (e.key === "End") go(list.length - 1);
  };

  return (
    <>
      <button
        ref={button}
        type="button"
        className={`btn btn-ghost btn-icon ${size === "sm" ? "btn-sm" : ""}`}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => (e.stopPropagation(), setOpen((o) => !o))}
      >
        <MoreHorizontal size={16} aria-hidden />
      </button>
      {open && (
        <Floating
          ref={menu}
          anchor={button}
          align="end"
          onDismiss={() => close(false)}
          role="menu"
          aria-label={label}
          onKeyDown={onKeyDown}
          onClick={(e) => e.stopPropagation()}
          minWidth={180}
          className="flex flex-col p-1"
        >
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              tabIndex={-1}
              className={itemClass}
              onClick={() => (close(), item.onSelect())}
            >
              {item.label}
            </button>
          ))}
        </Floating>
      )}
    </>
  );
}
